import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
} from '@mui/material';
import {
  Email as EmailIcon,
  Chat as ChatIcon,
  Mic as MicIcon,
} from '@mui/icons-material';
import { CHANNEL_TYPES_CONFIG } from '../../helpers/constants';

interface ChannelTypeSelectionViewProps {
  onChannelTypeSelect: (channelType: 'email' | 'message' | 'voice') => void;
}

const ChannelTypeSelectionView: React.FC<ChannelTypeSelectionViewProps> = ({ onChannelTypeSelect }) => {
  // Map icon names to actual icon components
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'Email':
        return <EmailIcon sx={{ fontSize: 40, color: '#1976d2' }} />;
      case 'Chat':
        return <ChatIcon sx={{ fontSize: 40, color: '#4caf50' }} />;
      case 'Mic':
        return <MicIcon sx={{ fontSize: 40, color: '#ff9800' }} />;
      default:
        return <EmailIcon sx={{ fontSize: 40 }} />;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        py: 2,
      }}
      data-testid="channel-type-selection-view"
    >
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 1,
        }}
        data-testid="channel-type-selection-subtitle"
      >
        Choose the type of channel you want to create
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {CHANNEL_TYPES_CONFIG.map((channelType) => {
          const isDisabled = channelType.id === 'voice';
          return (
          <Card
            key={channelType.id}
            onClick={() => !isDisabled && onChannelTypeSelect(channelType.id as 'email' | 'message' | 'voice')}
            sx={{
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              opacity: isDisabled ? 0.5 : 1,
              ...(isDisabled ? {} : {
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                  borderColor: channelType.color,
                },
              }),
            }}
            data-testid={`channel-type-card-${channelType.id}`}
          >
            <CardContent
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                p: 3,
                '&:last-child': {
                  pb: 3,
                },
              }}
            >
              {/* Icon Container - Left Side */}
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  minWidth: 64,
                  borderRadius: 1.5,
                  backgroundColor: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
                data-testid={`channel-type-icon-${channelType.id}`}
              >
                {getIcon(channelType.icon)}
              </Box>

              {/* Title and Description - Right Side */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  flex: 1,
                }}
              >
                {/* Title */}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 0.5,
                  }}
                  data-testid={`channel-type-label-${channelType.id}`}
                >
                  {channelType.label}
                </Typography>

                {/* Description */}
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.5,
                  }}
                  data-testid={`channel-type-description-${channelType.id}`}
                >
                  {channelType.description}
                </Typography>
              </Box>
            </CardContent>
          </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default ChannelTypeSelectionView;

